// src/components/admin/MemberList.js
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, CardContent } from '../ui/card';
import api from '../../utils/api';
import { showToast } from '../../utils/toast';
import MemberEditModal from './MemberEditModal';

/**
 * MemberList component displays organization members in a table with actions
 * 
 * @param {Object[]} members - Array of member objects
 * @param {string} currentUserId - ID of the current user
 * @param {Function} onMemberRemoved - Callback function when a member is removed
 * @param {Function} onEditMember - Callback function when edit button is clicked
 * @returns {JSX.Element} Rendered component
 */
const MemberList = ({ members, currentUserId, onMemberRemoved, onEditMember }) => {
  const [removingMemberId, setRemovingMemberId] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  
  // Handle removing a member
  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) {
      return;
    }
    
    setRemovingMemberId(memberId);
    
    try {
      await api.delete(`/organizations/members/${memberId}`);
      showToast.success('Member removed successfully');
      
      // Call the callback to update parent component state
      if (onMemberRemoved && typeof onMemberRemoved === 'function') {
        onMemberRemoved(memberId);
      }
    } catch (error) {
      console.error('Error removing member:', error);
      showToast.error(error.response?.data?.message || 'Failed to remove member');
    } finally {
      setRemovingMemberId(null);
    }
  };
  
  // Handle edit button click
  const handleEditClick = (member) => {
    setSelectedMember(member);
    setEditModalOpen(true);
  };
  
  // Handle member update
  const handleMemberUpdated = (updatedMember) => {
    // Update the member in the local state (if needed in the future)
    
    // Call the callback to update parent component state
    if (onEditMember && typeof onEditMember === 'function') {
      onEditMember(updatedMember);
    }
  };
  
  // Render loading state if no members
  if (!members || members.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-gray-500">No team members found</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <>
      <Card>
        <CardContent className="p-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {members.map((member) => (
                <tr key={member._id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{member.name}</div>
                    <div className="text-sm text-gray-500">@{member.username}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{member.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      member.role === 'admin' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {member.role === 'admin' ? 'Admin' : 'Employee'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      member.isEmailVerified 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {member.isEmailVerified ? 'Verified' : 'Pending'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button 
                      className="text-blue-600 hover:text-blue-900 mr-3"
                      onClick={() => handleEditClick(member)}
                    >
                      Edit
                    </button>
                    {member._id !== currentUserId && (
                      <button 
                        className="text-red-600 hover:text-red-900"
                        onClick={() => handleRemoveMember(member._id)}
                        disabled={removingMemberId === member._id}
                      >
                        {removingMemberId === member._id ? 'Removing...' : 'Remove'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      
      {selectedMember && (
        <MemberEditModal
          member={selectedMember}
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onMemberUpdated={handleMemberUpdated}
        />
      )}
    </>
  );
};

MemberList.propTypes = {
  members: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      username: PropTypes.string,
      email: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
      isEmailVerified: PropTypes.bool
    })
  ).isRequired,
  currentUserId: PropTypes.string.isRequired,
  onMemberRemoved: PropTypes.func,
  onEditMember: PropTypes.func
};

export default MemberList;